/**
 * Per-client preview-mode opt-out, paired with `PREVIEW_SKIP_COOKIE` in
 * `server/middleware/auth.ts`.
 *
 * `VITE_PREVIEW_MODE` (frontend) and `PREVIEW_MODE` (backend) make the dev
 * server *capable* of running as the seeded preview user. This module lets a
 * single browser independently leave that session — by setting a cookie +
 * localStorage flag — so a human can log in as a real Auth0 account while
 * AI agents on the same server keep getting the preview user.
 *
 * The cookie is the load-bearing half — it's the only signal the backend can
 * read. localStorage is a recovery hint for when the cookie gets dropped by
 * a cross-site redirect (Auth0) or browser cookie policy: at boot we detect
 * the drift and re-issue the cookie so frontend and backend stop disagreeing.
 *
 * Lifecycle:
 *   - boot: `?preview=on` query param wipes the flag (re-entry from real auth)
 *   - boot: if localStorage says we exited but the cookie is gone, restore it
 *   - boot: `DevAuthProvider` reads `isPreviewSessionActive()` once
 *   - logout from preview: `exitPreviewSession()` sets the flag and reloads
 *   - logout-then-back: visit `/?preview=on` to re-enter preview
 */

const PREVIEW_SKIP_KEY = 'dt-skip-preview';
const PREVIEW_SKIP_COOKIE_ATTRS = 'path=/; max-age=86400; samesite=lax';

export function isPreviewModeAllowed(): boolean {
  return import.meta.env.VITE_PREVIEW_MODE === 'true';
}

function hasSkipCookie(): boolean {
  if (typeof document === 'undefined') return false;
  return document.cookie
    .split(';')
    .some(cookie => cookie.trim() === `${PREVIEW_SKIP_KEY}=1`);
}

function hasSkipLocalStorage(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(PREVIEW_SKIP_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSkipCookie(): void {
  if (typeof document === 'undefined') return;
  document.cookie = `${PREVIEW_SKIP_KEY}=1; ${PREVIEW_SKIP_COOKIE_ATTRS}`;
}

function readSkipFlag(): boolean {
  if (typeof window === 'undefined') return false;
  return hasSkipCookie() || hasSkipLocalStorage();
}

export function isPreviewSessionActive(): boolean {
  return isPreviewModeAllowed() && !readSkipFlag();
}

export function exitPreviewSession(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PREVIEW_SKIP_KEY, '1');
  } catch {
    // Storage may be blocked (private mode, quota); cookie alone still gets
    // the backend to fall through, so the next request 401s and the user is
    // bounced through Auth0 — degraded but not broken.
  }
  writeSkipCookie();
  window.location.reload();
}

export function enterPreviewSession(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(PREVIEW_SKIP_KEY);
  } catch {
    // See exitPreviewSession — cookie clear below is the load-bearing part.
  }
  document.cookie = `${PREVIEW_SKIP_KEY}=; path=/; max-age=0`;
  window.location.href = '/';
}

// Boot-time logic. Runs at module evaluation so it fires before any provider
// mounts. Two cases, in order:
//
//   1) `?preview=on` is the escape hatch back into preview from a real-auth
//      session — clear both flags and reload on the same path.
//   2) Otherwise, if localStorage says we exited preview but the cookie is
//      gone, restore the cookie. The cookie is the only half the backend
//      reads, so without this the user thinks they exited preview while the
//      server keeps injecting the preview user. Likely culprits: an Auth0
//      cross-site redirect dropped a `SameSite=Lax` cookie on http, or some
//      browser policy cleared the cookie jar but kept localStorage.
if (typeof window !== 'undefined' && isPreviewModeAllowed()) {
  const params = new URLSearchParams(window.location.search);
  if (params.get('preview') === 'on') {
    try {
      window.localStorage.removeItem(PREVIEW_SKIP_KEY);
    } catch {
      // See exitPreviewSession — cookie clear below is the load-bearing part.
    }
    document.cookie = `${PREVIEW_SKIP_KEY}=; path=/; max-age=0`;
    params.delete('preview');
    const queryString = params.toString();
    const newUrl =
      window.location.pathname +
      (queryString ? `?${queryString}` : '') +
      window.location.hash;
    window.location.replace(newUrl);
  } else if (hasSkipLocalStorage() && !hasSkipCookie()) {
    writeSkipCookie();
  }
}
